import Image from 'next/image'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {ReactElement} from 'react'
import {clsx} from 'clsx'
import {
	Icon as CIcon,
	type IconSize,
} from '@/products/flexible-web-toolkit/components/icon'
import {
	getColor,
	getColorByAlias,
} from '@/products/flexible-web-toolkit/colors/registry'
import {defaultMargins} from '@/products/flexible-web-toolkit/styles'
import {loadImageFromKontentAI} from '@/image-loader'
import type {BlockWithClassName} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'

type IconListItemContentItem = IContentItem<{
	icon: Elements.AssetsElement
	brand: Elements.MultipleChoiceElement
	brandColor: Elements.CustomElement
	iconSize: Elements.MultipleChoiceElement
	title: Elements.TextElement
	text: Elements.RichTextElement
	squareIcon: Elements.MultipleChoiceElement
}>

export type IconListContentItem = IContentItem<{
	items: Elements.LinkedItemsElement<IconListItemContentItem>
	title: Elements.TextElement
	horizontal: Elements.MultipleChoiceElement
	margin: Elements.MultipleChoiceElement
}>

function getColorName(
	brandColorValue: string | undefined,
	brandCodename: string | undefined
): string | undefined {
	return brandColorValue || brandCodename
}

function getIconBrand(colorName: string | undefined): string {
	if (!colorName) return 'none'
	const resolved = getColor(colorName) || getColorByAlias(colorName)
	return resolved?.id ?? colorName
}

function getIconCustomClasses(colorName: string | undefined): string {
	if (!colorName) return ''
	const resolved = getColor(colorName) || getColorByAlias(colorName)
	return resolved ? `border-${resolved.id}` : ''
}

const IconListItem = ({
	block: item,
	page,
	isHorizontal,
	...context
}: Parameters<BlockWithClassName<IconListItemContentItem>>[0] & {
	isHorizontal: boolean
}): ReactElement => {
	const colorName = getColorName(
		item.elements.brandColor || undefined,
		item.elements.brand[0]?.codename
	)

	return (
		<li
			className={clsx('mb-4 flex', isHorizontal && 'mr-3 flex-col')}
			data-kontent-item-id={item.system.id}
			key={item.system.id}
		>
			<CIcon
				brand={getIconBrand(colorName)}
				className="flex-shrink-0"
				iconCustomClasses={getIconCustomClasses(colorName)}
				noBorder
				showSquareIcon={item.elements.squareIcon[0]?.codename === 'yes'}
				size={(item.elements.iconSize[0]?.codename || 'lg') as IconSize}
			>
				<div className="relative h-full w-full">
					{item.elements.icon[0] ? (
						<Image
							alt={item.elements.icon[0].description ?? ''}
							data-kontent-element-codename="icon"
							fill
							loader={loadImageFromKontentAI}
							src={item.elements.icon[0].url}
						/>
					) : null}
				</div>
			</CIcon>
			<div className="ml-2">
				<div className="font-bold">{item.elements.title}</div>
				{item.elements.text.value !== '<p><br></p>' && (
					<RichTextRenderer
						element={item.elements.text}
						page={page}
						{...context}
					/>
				)}
			</div>
		</li>
	)
}

export const IconListBlock: BlockWithClassName<IconListContentItem> = ({
	block,
	className,
	page,
	...context
}) => {
	const isHorizontal = block.elements.horizontal[0]?.codename === 'yes'

	return (
		<div>
			{block.elements.title ? <h4>{block.elements.title}</h4> : null}
			<ul
				className={clsx(
					'container-narrow container',
					isHorizontal && 'm-0 mb-2 flex flex-wrap p-0',
					className ? className : defaultMargins,
					block.elements.margin[0]?.codename === 'no'
						? ''
						: defaultMargins
				)}
			>
				{block.elements.items.map((item) => (
					<IconListItem
						block={item}
						isHorizontal={isHorizontal}
						key={item.system.id}
						page={page}
						{...context}
					/>
				))}
			</ul>
		</div>
	)
}
